import React, { useState } from 'react';
import { nfpApi } from '../../../services/nfpApi';
import { useNfpReg } from '../../../contexts/NfpRegContext';
import { ROLE_OPTIONS } from './roleOptions';
import { getAuthToken } from '../../../api/client';

export default function Step1Account({ onNext }) {
  const { draft, registration, setDraft, setRegistration } = useNfpReg();
  const [form, setForm] = useState({
    fullName: draft?.account?.fullName || '',
    email: draft?.account?.email || '',
    password: '',
    phone: draft?.account?.phone || '',
    role: draft?.account?.role || ROLE_OPTIONS[0].value,
    isSignatory: draft?.account?.isSignatory === false ? 'no' : 'yes',
    inviteSignatory: {
      name: draft?.account?.inviteSignatory?.name || '',
      email: draft?.account?.inviteSignatory?.email || '',
      title: draft?.account?.inviteSignatory?.title || '',
    },
    partOfNetwork: draft?.account?.partOfNetwork ? 'yes' : 'no',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const hasExistingSession = !!(registration?._id || getAuthToken());

  const onChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const applicantPayload = {
        name: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role,
        isSignatory: form.isSignatory === 'yes',
        partOfNetwork: form.partOfNetwork === 'yes',
        signatoryInvite: form.isSignatory === 'no'
          ? {
              name: form.inviteSignatory.name?.trim() || '',
              email: form.inviteSignatory.email?.trim() || '',
              title: form.inviteSignatory.title?.trim() || '',
            }
          : undefined,
      };

      let nextRegistration = registration;
      if (!hasExistingSession) {
        await nfpApi.startPendingRegistration({
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          password: form.password,
          role: 'admin',
        });
      }

      try {
        if (!nextRegistration?._id) {
          const started = await nfpApi.startRegistration();
          if (started?.registration) {
            nextRegistration = started.registration;
            setRegistration(started.registration);
          }
        }

        if (nextRegistration?._id) {
          try {
            const resp = await nfpApi.putApplicant(nextRegistration._id, applicantPayload);
            if (resp?.registration) {
              nextRegistration = resp.registration;
              setRegistration(resp.registration);
            }
          } catch (appErr) {
            console.warn('Failed to persist applicant info', appErr);
          }
        }
      } catch (startErr) {
        console.warn('Failed to bootstrap NFP registration', startErr);
      }

      // Cache useful bits for next step
      setDraft(d => ({
        ...d,
        account: {
          fullName: form.fullName.trim(),
          email: form.email,
          phone: form.phone,
          role: form.role,
          isSignatory: form.isSignatory === 'yes',
          inviteSignatory: form.isSignatory === 'no' ? {
            name: form.inviteSignatory.name?.trim() || '',
            email: form.inviteSignatory.email?.trim() || '',
            title: form.inviteSignatory.title?.trim() || '',
          } : null,
          partOfNetwork: form.partOfNetwork === 'yes',
        }
      }));

      onNext();
    } catch (e2) {
      setErr(e2?.message || 'Failed to create account.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} style={styles.form}>
      {err && <div style={styles.error}>{err}</div>}

      <div style={styles.intro}>
        Tell us about yourself and your role in the organization.
      </div>

      <div>
        <label>Your Full Name</label>
        <input
          autoComplete="name"
          value={form.fullName}
          onChange={(e) => onChange('fullName', e.target.value)}
          required
        />
      </div>

      <div style={styles.gridTwo}>
        <div>
          <label>Your Work Email</label>
          <input
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => onChange('email', e.target.value)}
            required
          />
        </div>
        <div>
          <label>Your Phone Number</label>
          <input
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            required
          />
        </div>
      </div>

      {!hasExistingSession && (
        <div>
          <label>Create Password</label>
          <input
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => onChange('password', e.target.value)}
            required
          />
        </div>
      )}

      <div>
        <label>Your Role at the Organization</label>
        <select value={form.role} onChange={(e) => onChange('role', e.target.value)}>
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.gridTwo}>
        <div>
          <label>Are you an Authorized Signatory?</label>
          <select value={form.isSignatory} onChange={(e) => onChange('isSignatory', e.target.value)}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div>
          <label>Is this organization part of a parent or chapter network?</label>
          <select value={form.partOfNetwork} onChange={(e) => onChange('partOfNetwork', e.target.value)}>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      </div>

      {form.isSignatory === 'no' && (
        <div style={styles.gridThree}>
          <div>
            <label>Signatory name</label>
            <input
              autoComplete="name"
              value={form.inviteSignatory.name}
              onChange={(e) =>
                onChange('inviteSignatory', { ...form.inviteSignatory, name: e.target.value })
              }
              required
            />
          </div>
          <div>
            <label>Signatory email</label>
            <input
              type="email"
              autoComplete="email"
              value={form.inviteSignatory.email}
              onChange={(e) =>
                onChange('inviteSignatory', { ...form.inviteSignatory, email: e.target.value })
              }
              required
            />
          </div>
          <div>
            <label>Title</label>
            <input
              autoComplete="organization-title"
              value={form.inviteSignatory.title}
              onChange={(e) =>
                onChange('inviteSignatory', { ...form.inviteSignatory, title: e.target.value })
              }
              required
            />
          </div>
        </div>
      )}

      <div style={styles.actions}>
        <button type="submit" disabled={busy} className="primary">
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </form>
  );
}

const styles = {
  form: {
    display: 'grid',
    gap: 18,
  },
  error: {
    background: 'rgba(248,113,113,0.16)',
    border: '1px solid rgba(248,113,113,0.45)',
    color: '#FCA5A5',
    padding: '10px 14px',
    borderRadius: 14,
    fontSize: 13,
  },
  intro: {
    fontSize: 14,
    lineHeight: 1.5,
    opacity: 0.86,
  },
  gridTwo: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  },
  gridThree: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    paddingTop: 8,
  },
};
